/**
 * Replaces the delivery fallback text with an estimated date range, counted in business days
 * (Monday to Friday) from today in Paris time. Dates are computed in the browser so cached pages
 * never show a stale range. Public holidays are not taken into account.
 */
class VarmierDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const minDays = Number(this.dataset.minDays);
    const maxDays = Number(this.dataset.maxDays);
    const template = this.dataset.template;
    const output = this.querySelector('[data-estimate-text]');

    if (!(output instanceof HTMLElement) || !template) return;
    if (!Number.isInteger(minDays) || !Number.isInteger(maxDays) || minDays < 1 || maxDays < minDays) return;

    const today = parisToday();
    // Dates are built at UTC midnight, so they are formatted in UTC to keep the same calendar day
    const format = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

    output.textContent = template
      .replace('[date_min]', format.format(addBusinessDays(today, minDays)))
      .replace('[date_max]', format.format(addBusinessDays(today, maxDays)));
  }
}

/**
 * @returns {Date} Today's calendar date in Paris, at UTC midnight
 */
function parisToday() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  /** @param {string} type */
  const part = (type) => Number(parts.find((p) => p.type === type)?.value);

  return new Date(Date.UTC(part('year'), part('month') - 1, part('day')));
}

/**
 * @param {Date} start
 * @param {number} days - Business days to add (Saturday and Sunday are skipped)
 * @returns {Date}
 */
function addBusinessDays(start, days) {
  const date = new Date(start);
  let added = 0;

  while (added < days) {
    date.setUTCDate(date.getUTCDate() + 1);
    const weekday = date.getUTCDay();
    if (weekday !== 0 && weekday !== 6) added += 1;
  }

  return date;
}

if (!customElements.get('varmier-delivery-estimate')) {
  customElements.define('varmier-delivery-estimate', VarmierDeliveryEstimate);
}
